import React, { useState } from 'react';
import * as Styles from '../styles';
import { Text, colors } from '../../../globalStyles';
import { UniversityLogo } from '../../../Components/Organization/UniversityLogo';
import { FinalButton } from '../../../Components/FinalButton';
import { Notification } from '../../../Components/Notification';
import { WarningWithIcon } from '../../../Components/Warning';
import { FormProvider, useForm } from 'react-hook-form';
import { InputRHF, DateInputRHF, NestedSelectRHF } from '../../../Components/Form';
import { Label } from '../../ProviderNetwork/EnrollModal/Common';
import { PermsOnly } from '../../../Components/Permissions/Only';
import { checkIfOver16, isValidEmail } from '../../../Components/utils';
import { useCurrentProvider } from '../../../Components/Permissions';
import { useHistory } from 'react-router-dom';
import { toaster } from 'baseui/toast';
import {
  Permission,
  ReferralPartners,
  ReferralSource,
  useCreatePartnerReferralMutation,
} from '../../../graphQL';
import { useOrganizationId } from '../../Organizations/util';
import moment from 'moment-timezone';

type TogetherAllFormFields = {
  firstName: string;
  lastName: string;
  birthDate: string;
  preferredName?: string;
  universityEmailAddress: string;
  confirmUniversityEmailAddress: string;
  organizationId?: number;
};

export function EnrollTogetherAll() {
  const required = 'This field is required.';
  const [showUnder16Warning, setShowUnder16Warning] = useState(false);
  const [showInvalidDomain, setShowInvalidDomain] = useState(false);
  const [showPreferredName, setShowPreferredName] = useState(false);
  const { currentProvider } = useCurrentProvider();

  const [createPartnerReferral, { error, loading }] = useCreatePartnerReferralMutation();

  const history = useHistory();
  const form = useForm<TogetherAllFormFields>();

  const pageOrgId = useOrganizationId();
  const providerOrganization = currentProvider.organizations.find(org => org.id === pageOrgId);
  const isSuperOrg = providerOrganization?.children && providerOrganization?.children.length > 0;

  const allowedEmailDomains = providerOrganization?.emailDomains;

  const submit = form.handleSubmit(async fields => {
    const validDomainEmail = validateDomainEmail(fields.universityEmailAddress);

    if (!providerOrganization) {
      return;
    }

    if (!validDomainEmail) {
      setShowInvalidDomain(true);
      return;
    }

    setShowInvalidDomain(false);

    const togetherAllReferral = await createPartnerReferral({
      variables: {
        data: {
          firstName: fields.firstName,
          lastName: fields.lastName,
          // format date to YYYY-MM-DD so its uniform across the database
          birthDate: moment(fields.birthDate, 'MM/DD/YYYY').format('YYYY-MM-DD'),
          preferredName: fields.preferredName,
          providerId: currentProvider.id,
          referralSource: ReferralSource.Mcp,
          partner: ReferralPartners.Togetherall,
          referralEmail: fields.universityEmailAddress,
          organizationId: fields.organizationId || providerOrganization.id,
        },
      },
    });
    if (togetherAllReferral) {
      toaster.positive('Referral submitted', { autoHideDuration: 3000 });
      history.push('/');
    }
  });

  const emailWatch = form.watch('universityEmailAddress');
  const confirmEmailWatch = form.watch('confirmUniversityEmailAddress');

  type ValidateResult = boolean | string;

  const validateDomainEmail = (value: string): ValidateResult => {
    if (allowedEmailDomains) {
      const domain = value.split('@')[1];
      if (allowedEmailDomains.includes(domain)) {
        return true;
      } else {
        return false;
      }
    }

    return false;
  };

  const validateDate = (value: string): ValidateResult => {
    if (checkIfOver16(value)) {
      setShowUnder16Warning(false);
      return true;
    } else {
      setShowUnder16Warning(true);
      return 'Must enter a valid date';
    }
  };

  const validateEmail = (value: string): ValidateResult => {
    if (isValidEmail(value)) {
      if (emailWatch !== confirmEmailWatch) {
        return 'Emails must match';
      }

      return true;
    } else {
      return 'Must enter a valid email';
    }
  };

  return (
    <Styles.Background>
      <Styles.Wrapper>
        <UniversityLogo />
        {error && <Notification kind="negative">{error.message}</Notification>}
        <Text.h1>Online Peer Support Referral</Text.h1>
        <PermsOnly allowed={Permission.PatientCreate}>
          <Text.h4grey>
            Online Peer Support referrals will be sent to our partner, Togetherall, and will not be
            managed by Mantra Health or within the MCP.
          </Text.h4grey>

          {showUnder16Warning && (
            <Styles.AlertBox className="mt4">
              <Text.h4 className="pa3" style={{ color: colors.danger }}>
                Student must be at least 16 years of age to access Togetherall.
              </Text.h4>
            </Styles.AlertBox>
          )}

          {showInvalidDomain && (
            <Styles.AlertBox className="mt4">
              <Text.h4 className="pa3" style={{ color: colors.danger }}>
                Invalid domain for this organization.
              </Text.h4>
            </Styles.AlertBox>
          )}

          <FormProvider {...form}>
            <Text.h2 className="mt4 mb4">
              Please let us know the name of the student you are referring to online peer support
            </Text.h2>
            <Styles.FormSection>
              <InputRHF
                name="firstName"
                controlProps={{ label: 'First Name', required: true }}
                rules={{ required }}
              />
              <InputRHF
                name="lastName"
                controlProps={{ label: 'Last Name', required: true }}
                rules={{ required }}
              />
              {showPreferredName ? (
                <Label name="preferredName" label="Preferred Name" className="mb0">
                  <InputRHF name="preferredName" />
                </Label>
              ) : (
                <Text.linkButton className="b tl" onClick={() => setShowPreferredName(true)}>
                  + Add Preferred Name
                </Text.linkButton>
              )}
            </Styles.FormSection>
            <Text.h2 className="mt4 mb4">Student Contact</Text.h2>
            <Styles.FormSection>
              <InputRHF
                name="universityEmailAddress"
                controlProps={{
                  label: 'Student University Email Address',
                  required: true,
                }}
                rules={{
                  validate: {
                    validateEmail,
                  },
                }}
              />
              <InputRHF
                name="confirmUniversityEmailAddress"
                controlProps={{
                  label: 'Confirm Student University Email',
                  required: true,
                }}
                rules={{
                  validate: {
                    validateEmail,
                  },
                }}
              />
              <DateInputRHF
                name="birthDate"
                controlProps={{
                  label: 'Student Date of Birth',
                  required: true,
                }}
                rules={{
                  validate: {
                    validateDate,
                  },
                }}
              />
              {isSuperOrg && (
                <NestedSelectRHF
                  controlProps={{ label: 'Campus?', required: true }}
                  rules={{ required: 'This field is required' }}
                  name="organizationId"
                  placeholder="Select campus"
                  options={providerOrganization.children.map(org => ({
                    id: org.id,
                    label: org.name,
                  }))}
                />
              )}
            </Styles.FormSection>
            <Styles.FormSection>
              <FinalButton
                type="submit"
                kind="primary"
                iconRight="iconsExternalLinkWhiteSvg"
                onClick={submit}
                loading={loading}
              >
                Submit
              </FinalButton>
            </Styles.FormSection>
          </FormProvider>

          <WarningWithIcon>
            <Text.bodySmallBold>
              Note: Upon clicking ‘Submit,’ the student will receive an email invite to our Online
              Peer Support partner, Togetherall.
            </Text.bodySmallBold>
          </WarningWithIcon>
        </PermsOnly>
      </Styles.Wrapper>
    </Styles.Background>
  );
}
